import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';
import {
	HdGraphSubType,
	HdGraphType,
} from 'src/app/chart/domain/hd-graph.domain';

import { DcBaseRequester } from '../core/dc-base.requester';
import { IChart, IChartResponse } from './chart.model';

@Injectable({
	providedIn: 'root',
})
export class ChartRequester extends DcBaseRequester<IChartResponse> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('CHARTS_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getChartList(
		includePublic: boolean,
		useCache = true
	): Observable<ReadonlyArray<IChartResponse>> {
		const resourceUrl = this.generateResourceUrl('CHARTS_GET_ALL_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				excludePublic: !includePublic,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					evictTimeoutInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IChartResponse>>(url, cachePolicy);
	}

	public getChart(
		chartId: number,
		type: HdGraphType,
		subType: HdGraphSubType | undefined
	): Observable<IChart> {
		const resourceUrl = this.generateResourceUrl(
			this.getApiKeyByType(type, subType)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: chartId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IChart>(url);
	}

	public changeActivationStatusChart(
		chart: IChart,
		hdGraphType: HdGraphType,
		hdGraphSubType: HdGraphSubType | undefined
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			this.getApiKeyByType(hdGraphType, hdGraphSubType)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: chart.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {
			metadata: {
				active: chart.metadata.actif,
				[camelToSnake('shortLabel')]: chart.metadata.label,
			},
		});
	}

	public deleteChart(
		chartId: number,
		hdGraphType: HdGraphType,
		hdGraphSubType: HdGraphSubType | undefined
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			this.getApiKeyByType(hdGraphType, hdGraphSubType)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: chartId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteManyCharts(chartIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('CHARTS_DELETE_BULK');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				chartsId: chartIds,
			}
		);
		return this.http.delete<void>(url);
	}

	public duplicateChart(
		chartId: number,
		hdGraphType: HdGraphType,
		hdGraphSubType: HdGraphSubType | undefined
	): Observable<number> {
		const resourceUrl = this.generateResourceUrl(
			this.getDuplicateApiKeyByType(hdGraphType, hdGraphSubType)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: chartId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.post<number>(url, {});
	}

	private getApiKeyByType(
		type: HdGraphType,
		subType: HdGraphSubType | undefined
	): string {
		if (type === HdGraphType.Barline) {
			return 'CHARTS_BAR_GET';
		}

		if (type === HdGraphType.Funnel) {
			return 'CHARTS_FUNNEL_GET';
		}

		if (type === HdGraphType.Indicator) {
			return 'CHARTS_INDICATOR_GET';
		}

		if (type === HdGraphType.Pie) {
			return 'CHARTS_PIE_GET';
		}

		if (type === HdGraphType.Range) {
			return 'CHARTS_RANGE_GET';
		}

		if (type === HdGraphType.Sankey) {
			return 'CHARTS_SANKEY_GET';
		}

		if (type === HdGraphType.Scatter) {
			return 'CHARTS_SCATTER_GET';
		}

		if (type === HdGraphType.Treemap) {
			return 'CHARTS_TREEMAP_GET';
		}

		if (type === HdGraphType.Gauge) {
			if (subType === HdGraphSubType.Bar) {
				return 'CHARTS_GAUGE_BAR_GET';
			}

			if (subType === HdGraphSubType.Circular) {
				return 'CHARTS_GAUGE_CIRCULAR_GET';
			}

			if (subType === HdGraphSubType.Linear) {
				return 'CHARTS_GAUGE_LINEAR_GET';
			}
		}

		throw new Error(`Unrecognized type: ${type}`);
	}

	private getDuplicateApiKeyByType(
		type: HdGraphType,
		subType: HdGraphSubType | undefined
	): string {
		if (type === HdGraphType.Barline) {
			return 'CHARTS_BAR_DUPLICATE';
		}

		if (type === HdGraphType.Funnel) {
			return 'CHARTS_FUNNEL_DUPLICATE';
		}

		if (type === HdGraphType.Indicator) {
			return 'CHARTS_INDICATOR_DUPLICATE';
		}

		if (type === HdGraphType.Pie) {
			return 'CHARTS_PIE_DUPLICATE';
		}

		if (type === HdGraphType.Range) {
			return 'CHARTS_RANGE_DUPLICATE';
		}

		if (type === HdGraphType.Sankey) {
			return 'CHARTS_SANKEY_DUPLICATE';
		}

		if (type === HdGraphType.Scatter) {
			return 'CHARTS_SCATTER_DUPLICATE';
		}

		if (type === HdGraphType.Treemap) {
			return 'CHARTS_TREEMAP_DUPLICATE';
		}

		if (type === HdGraphType.Gauge) {
			if (subType === HdGraphSubType.Bar) {
				return 'CHARTS_GAUGE_BAR_DUPLICATE';
			}

			if (subType === HdGraphSubType.Circular) {
				return 'CHARTS_GAUGE_CIRCULAR_DUPLICATE';
			}

			if (subType === HdGraphSubType.Linear) {
				return 'CHARTS_GAUGE_LINEAR_DUPLICATE';
			}
		}

		throw new Error(`Unrecognized type: ${type}`);
	}
}
